import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { OfflineProvider } from './context/OfflineProvider';
import { PlatformSettingsProvider } from './context/PlatformSettingsContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import Login from './pages/auth/Login';
import OwnerLogin from './pages/auth/OwnerLogin';
import Register from './pages/auth/Register';
import OwnerLayout from './layouts/OwnerLayout';
import AdminLayout from './layouts/AdminLayout';
import OwnerDashboard from './pages/owner/Dashboard';
import SubscriptionManagement from './pages/owner/subscriptions/SubscriptionManagement';
import SubscriptionPackages from './pages/owner/subscriptions/SubscriptionPackages';
import EditSubscription from './pages/owner/subscriptions/EditSubscription';
import UserManagement from './pages/owner/users/UserManagement';
import OwnerPanelUserManagement from './pages/owner/panel-users/OwnerPanelUserManagement';
import FinancialManagement from './pages/owner/finance/FinancialManagement';
import Notifications from './pages/owner/notifications/Notifications';
import OwnerReports from './pages/owner/reports/Reports';
import PaymentGateway from './pages/owner/payment-gateway/PaymentGateway';
import OwnerSettings from './pages/owner/settings/Settings';
import OwnerProfile from './pages/owner/Profile';
import AdminDashboard from './pages/admin/Dashboard';
import BranchManagement from './pages/admin/branches/BranchManagement';
import EditBranch from './pages/admin/branches/EditBranch';
import MemberManagement from './pages/admin/members/MemberManagement';
import EditMember from './pages/admin/members/EditMember';
import PackageManagement from './pages/admin/packages/PackageManagement';
import EditPackage from './pages/admin/packages/EditPackage';
import MembershipManagement from './pages/admin/memberships/MembershipManagement';
import TrainerManagement from './pages/admin/trainers/TrainerManagement';
import ReceptionistManagement from './pages/admin/receptionists/ReceptionistManagement';
import AttendanceManagement from './pages/admin/attendance/AttendanceManagement';
import FinanceManagement from './pages/admin/finance/FinanceManagement';
import CoaLedgerPage from './pages/admin/finance/CoaLedgerPage';
import AllModule from './pages/admin/all-module/AllModule';
import CustomerLedgerDetail from './pages/admin/all-module/CustomerLedgerDetail';
import AdminDetailedReport from './pages/admin/DetailedReport';
import PettyCashRangePage from './pages/admin/PettyCashRangePage';
import BankRangePage from './pages/admin/BankRangePage';
import ReportManagement from './pages/admin/reports/ReportManagement';
import PaymentMethodsManagement from './pages/admin/payment-methods/PaymentMethodsManagement';
import AdminNotifications from './pages/admin/notifications/Notifications';
import ReceptionistLayout from './layouts/ReceptionistLayout';
import ReceptionistMemberManagement from './pages/receptionist/members/MemberManagement';
import ReceptionistAttendance from './pages/receptionist/Attendance';
import ReceptionistMemberships from './pages/receptionist/Memberships';
import ReceptionistFreezeAccounts from './pages/receptionist/FreezeAccounts';
import ReceptionistPayments from './pages/receptionist/Payments';
import ReceptionistVisitors from './pages/receptionist/Visitors';
import ReceptionistRemainingPayments from './pages/receptionist/RemainingPayments';
import ReceptionistRemainingPaymentsReport from './pages/receptionist/RemainingPaymentsReport';
import ReceptionistClasses from './pages/receptionist/Classes';
import ReceptionistEquipments from './pages/receptionist/Equipments';
import ReceptionistTrainer from './pages/receptionist/Trainer';
import ReceptionistProfile from './pages/receptionist/Profile';
import ReceptionistSubscriptions from './pages/receptionist/Sales';
import DailyZReport from './pages/shared/DailyZReport';
import DailyZReport1 from './pages/shared/DailyZReport1';
import ReceptionistDashboard from './pages/receptionist/Dashboard';
import SaleRoutePage from './pages/receptionist/sales/SaleRoutePage';
import { RECEPTIONIST_SALE_PAGES } from './pages/receptionist/sales/salePageConfig';
import AdminSettings from './pages/admin/settings/AdminSettings';
import AdminPanelUserManagement from './pages/admin/panel-users/AdminPanelUserManagement';

// Admin profile route removed — /admin/profile redirects to settings
function App() {
  return (
    <AuthProvider>
    <OfflineProvider>
    <PlatformSettingsProvider>
    <Routes>
      <Route path="/" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/owner">
        <Route index element={<OwnerLogin />} />
        <Route element={
          <ProtectedRoute allowedRoles={['owner']} loginPath="/owner">
            <OwnerLayout />
          </ProtectedRoute>
        }>
          <Route path="dashboard" element={<OwnerDashboard />} />
          <Route path="packages" element={<SubscriptionPackages />} />
          <Route path="subscriptions" element={<SubscriptionManagement />} />
          <Route path="subscriptions/edit/:id" element={<EditSubscription />} />
          <Route path="users" element={<UserManagement />} />
          <Route path="user-management" element={<OwnerPanelUserManagement />} />
          <Route path="finance" element={<FinancialManagement />} />
          <Route path="notifications" element={<Notifications />} />
          <Route path="reports" element={<OwnerReports />} />
          <Route path="payment-gateway" element={<PaymentGateway />} />
          <Route path="settings" element={<OwnerSettings />} />
          <Route path="profile" element={<OwnerProfile />} />
        </Route>
      </Route>
      <Route path="/admin" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminLayout />
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<AdminDashboard />} />
        <Route path="branches" element={<BranchManagement />} />
        <Route path="branches/edit/:id" element={<EditBranch />} />
        <Route path="members" element={<MemberManagement />} />
        <Route path="user-management" element={<AdminPanelUserManagement />} />
        <Route path="members/edit/:id" element={<EditMember />} />
        <Route path="packages" element={<PackageManagement />} />
        <Route path="packages/edit/:id" element={<EditPackage />} />
        <Route path="membership-packages" element={<Navigate to="/admin/packages" replace />} />
        <Route path="memberships" element={<MembershipManagement />} />
        <Route path="subscriptions" element={<ReceptionistSubscriptions />} />
        <Route path="guest-pass" element={<ReceptionistVisitors />} />
        {RECEPTIONIST_SALE_PAGES.map(({ route }) => (
          <Route key={`admin-${route}`} path={route} element={<SaleRoutePage />} />
        ))}
        <Route path="remaining-payments" element={<ReceptionistRemainingPayments />} />
        <Route path="remaining-payments-report" element={<ReceptionistRemainingPaymentsReport />} />
        <Route path="freeze" element={<ReceptionistFreezeAccounts />} />
        <Route path="trainers" element={<TrainerManagement />} />
        <Route path="receptionists" element={<ReceptionistManagement />} />
        <Route path="attendance" element={<AttendanceManagement />} />
        <Route path="classes" element={<ReceptionistClasses />} />
        <Route path="equipments" element={<ReceptionistEquipments />} />
        <Route path="finance" element={<FinanceManagement />} />
        <Route path="finance/coa/:parentCode" element={<CoaLedgerPage />} />
        <Route path="finance/coa/:parentCode/:childCode" element={<CoaLedgerPage />} />
        <Route path="detailed-report" element={<AdminDetailedReport />} />
        <Route path="petty-cash" element={<PettyCashRangePage />} />
        <Route path="bank" element={<BankRangePage />} />
        <Route path="z-report" element={<DailyZReport />} />
        <Route path="z-report-1" element={<DailyZReport1 />} />
        <Route path="all-module" element={<AllModule />} />
        <Route path="all-module/:memberId" element={<CustomerLedgerDetail />} />
        <Route path="invoices" element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="payments" element={<ReceptionistPayments />} />
        <Route path="visitors" element={<Navigate to="/admin/guest-pass" replace />} />
        <Route path="reports" element={<ReportManagement />} />
        <Route path="notifications" element={<AdminNotifications />} />
        <Route path="payment-methods" element={<PaymentMethodsManagement />} />
        <Route path="settings" element={<AdminSettings />} />
        <Route path="profile" element={<Navigate to="settings" replace />} />
      </Route>
      <Route path="/receptionist" element={
        <ProtectedRoute allowedRoles={['receptionist']}>
          <ReceptionistLayout />
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<ReceptionistDashboard />} />
        <Route path="members" element={<ReceptionistMemberManagement />} />
        <Route path="subscriptions" element={<ReceptionistSubscriptions />} />
        <Route path="sales" element={<Navigate to="/receptionist/subscriptions" replace />} />
        <Route path="members/add" element={<Navigate to="/receptionist/members" replace />} />
        <Route path="members/edit/:id" element={<Navigate to="/receptionist/members" replace />} />
        <Route path="member-registration" element={<Navigate to="/receptionist/members" replace />} />
        <Route path="attendance" element={<ReceptionistAttendance />} />
        <Route path="memberships" element={<ReceptionistMemberships />} />
        <Route path="freeze" element={<ReceptionistFreezeAccounts />} />
        <Route path="guest-pass" element={<ReceptionistVisitors />} />
        {RECEPTIONIST_SALE_PAGES.map(({ route }) => (
          <Route key={route} path={route} element={<SaleRoutePage />} />
        ))}
        <Route path="remaining-payments" element={<ReceptionistRemainingPayments />} />
        <Route path="remaining-payments-report" element={<ReceptionistRemainingPaymentsReport />} />
        <Route path="notifications" element={<Navigate to="/admin/notifications" replace />} />
        <Route path="classes" element={<ReceptionistClasses />} />
        <Route path="equipments" element={<ReceptionistEquipments />} />
        <Route path="trainer" element={<ReceptionistTrainer />} />
        <Route path="lockers" element={<Navigate to="/receptionist/members" replace />} />
        <Route path="visitors" element={<Navigate to="/receptionist/guest-pass" replace />} />
        <Route path="payments" element={<Navigate to="/admin/payments" replace />} />
        <Route path="reports" element={<Navigate to="/admin/reports" replace />} />
        <Route path="profile" element={<ReceptionistProfile />} />
        <Route path="detailed-report" element={<AdminDetailedReport />} />
        <Route path="petty-cash" element={<PettyCashRangePage />} />
        <Route path="bank" element={<BankRangePage />} />
        <Route path="z-report" element={<DailyZReport />} />
        <Route path="z-report-1" element={<DailyZReport1 />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </PlatformSettingsProvider>
    </OfflineProvider>
    </AuthProvider>
  );
}

export default App;
